import React, { useEffect, useRef, useState } from "react";

import { FieldArray, Form, Formik } from "formik";
import { useParams } from "react-router-dom";

import CompellonApi from "apis/compellon";
import { Button, Pane, Toastr, Typography } from "clearsense-ui/components";
import { Plus } from "components/Common/clearsense-ui/icons";

import ConditionGroup from "./ConditionGroup";
import {
  DEFAULT_CONDITION_VALUES,
  NEW_WHAT_IF_INITIAL_VALUES,
  NEW_WHAT_IF_VALIDATION_SCHEMA,
} from "./constants";
import { handleWhatIfPayload } from "./Utils/apiDataConverters";

const NewWhatIf = ({
  isNewWhatIfPaneOpen,
  setIsNewWhatIfPaneOpen,
  conditions,
  fetchWhatIfs,
}) => {
  const { analysisId } = useParams();
  const [isConditionChanged, setConditionChanged] = useState(true);

  const buttonRef = useRef(null);

  useEffect(() => {
    buttonRef?.current?.scrollIntoView({ behavior: "smooth" });
  }, [isConditionChanged]);

  const handleSubmit = async ({ conditionGroups }) => {
    try {
      const payload = handleWhatIfPayload(conditionGroups, analysisId);
      await CompellonApi.createWhatIfAnalysis(payload);
      Toastr.success("What-If created successfully");
    } catch (error) {
      logger.error(error);
    } finally {
      setIsNewWhatIfPaneOpen(false);
      fetchWhatIfs();
    }
  };

  const handleAddition = arrayHelpers => {
    setConditionChanged(prevState => !prevState);
    arrayHelpers.push(DEFAULT_CONDITION_VALUES);
  };

  const handleClose = resetForm => {
    resetForm();
    setIsNewWhatIfPaneOpen(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={NEW_WHAT_IF_INITIAL_VALUES}
      onSubmit={handleSubmit}
      validationSchema={NEW_WHAT_IF_VALIDATION_SCHEMA}
    >
      {({ values: { conditionGroups }, submitForm, resetForm }) => (
        <Form noValidate>
          <Pane
            isOpen={isNewWhatIfPaneOpen}
            onClose={() => handleClose(resetForm)}
          >
            <Pane.Header>
              <Typography style="h3" type="bold" className="-mt-3">
                New What-If Analysis
              </Typography>
            </Pane.Header>
            <Pane.Body className="py-2 space-y-6">
              <FieldArray
                name="conditionGroups"
                render={arrayHelpers => {
                  const columnNames = Object.keys(conditions);
                  return (
                    <div className="w-full space-y-6">
                      {conditionGroups.map((_, index) => (
                        <ConditionGroup
                          key={`group_${index}`}
                          index={index}
                          arrayHelpers={arrayHelpers}
                          conditions={conditions}
                          columnNames={columnNames}
                        />
                      ))}
                      <Button
                        label="Add more conditions"
                        icon={Plus}
                        iconSize={24}
                        onClick={() => handleAddition(arrayHelpers)}
                        ref={buttonRef}
                        disabled={conditionGroups.length >= columnNames.length}
                      />
                    </div>
                  );
                }}
              />
            </Pane.Body>
            <Pane.Footer className="space-x-2">
              <Button
                label="Run"
                type="submit"
                onClick={() => submitForm()}
                disabled={conditionGroups.some(({ isSaved }) => !isSaved)}
              />
              <Button
                style="text"
                label="Cancel"
                onClick={() => handleClose(resetForm)}
              />
            </Pane.Footer>
          </Pane>
        </Form>
      )}
    </Formik>
  );
};

export default NewWhatIf;
